import tinycolor from 'tinycolor2';
import { siteTextStyles } from '../../shared/theme/themeTypography';

/**
 * Couleurs de secours quand aucune palette de document ne s'applique : des
 * neutres seulement. Les teintes viennent de la charte (document ou template),
 * jamais d'une gamme générique qui n'appartient à personne.
 */
export const DEFAULT_RICH_TEXT_SWATCHES = ['#000000', '#495057', '#adb5bd', '#ffffff'];

function resolveStyle(colorObj) {
  if (colorObj == null) return null;
  if (typeof colorObj === 'string') return colorObj === 'transparent' ? null : colorObj;
  return colorObj.style || null;
}

/**
 * Normalise une couleur CSS (hex opaque ou rgba / hexa si alpha < 1).
 * @param {string} cssColor
 * @returns {string|null}
 */
export function normalizeCssColor(cssColor) {
  if (cssColor == null || cssColor === '' || cssColor === 'transparent') return null;
  const c = tinycolor(cssColor);
  if (!c.isValid()) return null;
  if (c.getAlpha() >= 1) return c.toHexString().toLowerCase();
  const { r, g, b, a } = c.toRgb();
  const alpha = Math.round(a * 1000) / 1000;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/**
 * Normalise une couleur CSS en hex minuscule (alpha ignoré).
 * @param {string} cssColor
 * @returns {string|null}
 */
export function normalizeToHex(cssColor) {
  const normalized = normalizeCssColor(cssColor);
  if (!normalized) return null;
  const c = tinycolor(normalized);
  if (!c.isValid()) return null;
  return c.toHexString().toLowerCase();
}

/**
 * Extrait les couleurs du proposal (configuration éditeur) pour la palette Tiptap.
 * Déduplique par valeur RGB/hex (une seule entrée par couleur normalisée).
 *
 * @param {object} configuration - `proposal.content.configuration`
 * @returns {{ label: string, value: string }[]} `value` = hex
 */
export function extractProposalRichTextPalette(configuration) {
  if (!configuration || typeof configuration !== 'object') return [];

  const byHex = new Map();

  const add = (raw, label) => {
    const hex = normalizeToHex(raw);
    if (!hex || byHex.has(hex)) return;
    byHex.set(hex, label || hex);
  };

  (configuration.colors || []).forEach((c, i) => {
    const raw = resolveStyle(c);
    const label = (c && typeof c === 'object' && c.label) ? String(c.label) : `Couleur ${i + 1}`;
    add(raw, label);
  });

  add(resolveStyle(configuration.baseFontColor), 'Texte par défaut');

  if (typeof configuration.primaryColor === 'string') {
    add(configuration.primaryColor, 'Primaire');
  } else {
    add(resolveStyle(configuration.primaryColor), 'Primaire');
  }

  ['title1', 'title2', 'title3', 'title4'].forEach((key) => {
    const tc = configuration[key];
    if (!tc || typeof tc !== 'object') return;
    add(resolveStyle(tc.color), `${key} — texte`);
    add(resolveStyle(tc.backgroundColor), `${key} — fond`);
  });

  return Array.from(byHex.entries()).map(([value, label]) => ({ label, value }));
}

/**
 * Reconstruit une palette minimale depuis proposalStyles / thème (sites sans richTextPalette).
 *
 * @param {object} [proposalStyles]
 * @param {object} [theme]
 * @returns {{ label: string, value: string }[]}
 */
export function buildRichTextPaletteFromProposalStyles(proposalStyles, theme = {}) {
  const byHex = new Map();
  const add = (raw, label) => {
    const hex = normalizeToHex(raw);
    if (!hex || byHex.has(hex)) return;
    byHex.set(hex, label || hex);
  };

  if (proposalStyles && typeof proposalStyles === 'object') {
    add(proposalStyles.baseFontColor, 'Texte par défaut');
    ['title1', 'title2', 'title3', 'title4'].forEach((key) => {
      const t = proposalStyles[key];
      if (!t || typeof t !== 'object') return;
      add(t.color, `${key} — texte`);
      add(t.backgroundColor, `${key} — fond`);
    });
  }

  if (theme && typeof theme === 'object') {
    add(theme.primaryColor, 'Primaire (thème)');
    add(theme.headingColor, 'Titres (thème)');
  }

  return Array.from(byHex.entries()).map(([value, label]) => ({ label, value }));
}

/**
 * Palette effective pour l’éditeur web : configuration proposal > richTextPalette stockée > styles.
 *
 * @param {object} [projectConfig]
 * @param {object} [configuration] - `proposal.content.configuration` (live)
 * @returns {{ label: string, value: string }[]}
 */
export function resolveProposalRichTextPalette(projectConfig, configuration = null) {
  if (configuration && typeof configuration === 'object') {
    const fromLive = extractProposalRichTextPalette(configuration);
    if (fromLive.length > 0) return fromLive;
  }

  const stored = projectConfig?.richTextPalette;
  if (Array.isArray(stored) && stored.length > 0) return stored;

  return buildRichTextPaletteFromProposalStyles(
    siteTextStyles(projectConfig),
    projectConfig?.theme,
  );
}

/**
 * Fusionne couleurs template, palette proposal et défauts, sans doublon hex.
 *
 * @param {{
 *   proposalPalette?: { label: string, value: string }[],
 *   templateCustomColors?: ({ label: string, value: string }|string)[],
 *   extraDefaults?: string[],
 * }} opts
 * @returns {{ label: string, value: string }[]}
 */
export function mergeRichTextSwatches({
  proposalPalette = [],
  templateCustomColors = [],
  extraDefaults = DEFAULT_RICH_TEXT_SWATCHES,
  includeDefaults = true,
}) {
  const byColor = new Map();

  const push = (cssColor, label) => {
    const normalized = normalizeCssColor(cssColor);
    if (!normalized || byColor.has(normalized)) return;
    byColor.set(normalized, label || normalized);
  };

  (templateCustomColors || []).forEach((color) => (
    typeof color === 'string' ? push(color, 'Template') : push(color?.value, color?.label)
  ));
  (proposalPalette || []).forEach((p) => push(p.value, p.label));
  if (includeDefaults) {
    (extraDefaults || []).forEach((h) => push(h, ''));
  }

  return Array.from(byColor.entries()).map(([value, label]) => ({ label, value }));
}
